<script setup lang="ts">
import { QuestionMarkCircleIcon } from "@heroicons/vue/24/outline";
import { useVModel } from "@vueuse/core";
import ProgressSpinner from "primevue/progressspinner";
import Tooltip, { type TooltipOptions } from "primevue/tooltip";
import { computed, ref, toRaw } from "vue";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import CreateEditTodosModal from "../../../../modals/todos/CreateEditTodosModal.vue";
import TodosTable from "./components/TodosTable/index.vue";
import { TODOS_TOOLTIP_MESSAGE } from "./constants";
import type { SessionTodoForm, TodosSectionProps, WysiwygContent } from "./types";

const props = withDefaults(defineProps<TodosSectionProps>(), {
  modelValue: () => [],
  loading: false,
});

const emit = defineEmits<(e: "update:modelValue", value: SessionTodoForm[]) => void>();

const sessionTodos = useVModel(props, "modelValue", emit);
const modalOpen = ref<boolean>(false);

const sessionTodoInitialState = (): SessionTodoForm => ({
  todo_id: null,
  required: true,
  placement: "Onboarding",
  ranking: 2,
  display_name: null,
  due_date: null,
  instructions: {} as WysiwygContent,
  supporting_url: null,
});

const createEditTodo = ref<SessionTodoForm>(sessionTodoInitialState());
const isEditingTodo = ref<boolean>(false);

const highPrioritySessionTodos = computed(() => (sessionTodos.value ?? []).filter((todo) => todo.ranking === 1));
const normalPrioritySessionTodos = computed(() =>
  (sessionTodos.value ?? []).filter((todo) => todo.ranking === 0 || todo.ranking === 2),
);

const handleEditTodo = (todo: SessionTodoForm): void => {
  createEditTodo.value = structuredClone(toRaw(todo));
  isEditingTodo.value = true;
  modalOpen.value = true;
};

const removeTodo = (): void => {
  sessionTodos.value = (sessionTodos.value ?? []).filter((todo) => todo.todo_id !== createEditTodo.value.todo_id);
  closeModal();
};

const saveTodo = (): void => {
  const next = [...(sessionTodos.value ?? [])];
  if (isEditingTodo.value) {
    const index = next.findIndex((todo) => todo.todo_id === createEditTodo.value.todo_id);
    if (index !== -1) {
      next[index] = createEditTodo.value;
    }
  } else {
    next.push(createEditTodo.value);
  }
  sessionTodos.value = next;
  closeModal();
};

const closeModal = (): void => {
  createEditTodo.value = sessionTodoInitialState();
  isEditingTodo.value = false;
  modalOpen.value = false;
};

const vTooltip = Tooltip;
const tooltipSetup: TooltipOptions = {
  value: TODOS_TOOLTIP_MESSAGE,
  id: "todos-section-tooltip",
  fitContent: true,
  class: "bg-white shadow rounded white max-w-sm",
  hideDelay: 300,
  pt: {
    text: "text-xs text-indigo-base p-3 leading-none break-words",
  },
};
</script>

<template>
  <div class="grid grid-cols-1">
    <ExpandingSection field-label="To-Dos" input-classes="!p-6">
      <template #content>
        <div class="flex flex-wrap items-center justify-between gap-2 pb-2">
          <span data-testid="todos-count-title" class="flex items-center text-indigo-base font-montserrat text-base font-semibold" >
            To-Dos ({{ sessionTodos?.length ?? 0 }})
            <span v-tooltip.top="tooltipSetup" class="ml-2">
              <QuestionMarkCircleIcon class="h-6 w-6" />
            </span>
          </span>
          <PlusIcon
            data-testid="todos-add-button"
            class="text-teal-900"
            :class="!props.hasEditPermissions ? 'opacity-30' : 'cursor-pointer'"
            @click="modalOpen = hasEditPermissions"
          />
        </div>
        <div v-if="props.loading" data-testid="todos-loading-spinner" class="flex justify-center items-center">
          <ProgressSpinner style="width: 45px; height: 45px;" />
        </div>
        <template v-else>
          <TodosTable
            data-testid="todos-high-priority-table"
            :items="highPrioritySessionTodos"
            :has-edit-permissions="props.hasEditPermissions"
            header-label-text="High Priority"
            header-label-class="bg-red-light text-red-700"
            class="pb-2"
            @edit-todo="handleEditTodo"
          />
          <TodosTable
            data-testid="todos-normal-priority-table"
            :items="normalPrioritySessionTodos"
            :has-edit-permissions="props.hasEditPermissions"
            header-label-text="Normal Priority"
            header-label-class="bg-[#DEF0FD] text-blue-selection"
            @edit-todo="handleEditTodo"
          />
        </template>

        <CreateEditTodosModal
          data-testid="create-edit-todos-modal"
          :open="modalOpen"
          :model-value="createEditTodo"
          :edit="isEditingTodo"
          :current-todos="sessionTodos ?? []"
          @close-modal="closeModal"
          @save-todo="saveTodo"
          @remove-todo="removeTodo"
        />
      </template>
    </ExpandingSection>
  </div>
</template>

<style scoped>
::v-deep(.progress-spinner-circle) {
  @apply !stroke-gray-400;
}
</style>
